import { useApi } from "@excellent/ui/api";
import { checkResults } from "@excellent/ui/api/contracts/check-results";

export function CheckResultProof({
  resultRecordId,
  workflowId,
}: {
  resultRecordId: number;
  workflowId?: string;
}) {
  const result = useApi(checkResults.get, { params: { id: resultRecordId } });
  const record = result.data;
  const mismatched = workflowId != null && record != null && record.workflow.id !== workflowId;

  return (
    <section
      className="vp-stack"
      data-gap="sm"
      data-check-result-proof="true"
      data-check-result-id={resultRecordId}
      aria-labelledby="check-result-proof-title"
    >
      <div>
        <div className="vp-card-title" id="check-result-proof-title">
          Exact check result
        </div>
        <div className="vp-card-sub">Retained result and explanation record #{resultRecordId}</div>
      </div>

      {result.isLoading && !record ? (
        <div className="vp-callout" data-tone="neutral">
          <div className="vp-callout-title">Loading the exact result</div>
          <div className="vp-callout-body">Reading this workspace's retained activity record.</div>
        </div>
      ) : result.error ? (
        <div className="vp-callout" data-tone="fail">
          <div className="vp-callout-title">Exact result unavailable</div>
          <div className="vp-callout-body">
            No result is shown because this record could not be read in the current workspace.
          </div>
          <button type="button" data-action-id="check-result.retry" onClick={() => void result.mutate()}>
            Retry
          </button>
        </div>
      ) : mismatched ? (
        <div className="vp-callout" data-tone="fail">
          <div className="vp-callout-title">Result does not belong to this workflow</div>
          <div className="vp-callout-body">
            The requested result was retained for another workflow, so its details are hidden here.
          </div>
        </div>
      ) : record ? (
        <>
          <div
            className="vp-callout"
            data-tone={record.outcome.status === "PASS" ? "pass" : "fail"}
          >
            <div className="vp-callout-title">{record.outcome.status}</div>
            <div className="vp-callout-body">
              {record.gate.label} finished with {record.outcome.passed} passing and{" "}
              {record.outcome.failed} failing gates at {record.outcome.ranAt}.
            </div>
          </div>

          <dl className="vp-fields">
            <div className="vp-field">
              <dt className="vp-field-label">Candidate</dt>
              <dd className="vp-field-value vp-mono">{record.candidate.id}</dd>
            </div>
            <div className="vp-field">
              <dt className="vp-field-label">Observation review</dt>
              <dd className="vp-field-value vp-mono">{record.candidate.reviewId}</dd>
            </div>
            <div className="vp-field">
              <dt className="vp-field-label">Workflow</dt>
              <dd className="vp-field-value vp-mono">{record.workflow.id}</dd>
            </div>
            <div className="vp-field">
              <dt className="vp-field-label">Project / app</dt>
              <dd className="vp-field-value">
                {record.project.name} <span className="vp-mono">#{record.project.appId}</span>
              </dd>
            </div>
            <div className="vp-field">
              <dt className="vp-field-label">Project root</dt>
              <dd className="vp-field-value vp-mono">{record.project.root}</dd>
            </div>
            <div className="vp-field">
              <dt className="vp-field-label">Gate</dt>
              <dd className="vp-field-value">
                {record.gate.label} <span className="vp-mono">({record.gate.id})</span>
              </dd>
            </div>
            <div className="vp-field">
              <dt className="vp-field-label">Command</dt>
              <dd className="vp-field-value vp-mono">{record.gate.command}</dd>
            </div>
            <div className="vp-field">
              <dt className="vp-field-label">Duration</dt>
              <dd className="vp-field-value">{record.outcome.durationMs} ms</dd>
            </div>
          </dl>

          <div className="vp-callout" data-tone="neutral">
            <div className="vp-callout-title">Evidence and source basis</div>
            <div className="vp-callout-body">{record.evidence.basis}</div>
            <div className="vp-card-sub">
              Source: retained workflow evidence snapshot. {record.evidence.limitation}
            </div>
          </div>

          {record.receipt.present ? (
            <div className="vp-callout" data-tone="pass">
              <div className="vp-callout-title">Signed receipt</div>
              <div className="vp-callout-body">{record.receipt.limitation}</div>
              <dl className="vp-fields">
                <div className="vp-field">
                  <dt className="vp-field-label">Receipt</dt>
                  <dd className="vp-field-value vp-mono">{record.receipt.receiptId}</dd>
                </div>
                <div className="vp-field">
                  <dt className="vp-field-label">Payload digest</dt>
                  <dd className="vp-field-value vp-mono">{record.receipt.receiptPayloadDigest}</dd>
                </div>
                <div className="vp-field">
                  <dt className="vp-field-label">Accepted contract</dt>
                  <dd className="vp-field-value vp-mono">
                    {record.receipt.acceptedContractDigest}
                  </dd>
                </div>
                <div className="vp-field">
                  <dt className="vp-field-label">Attempt</dt>
                  <dd className="vp-field-value vp-mono">{record.receipt.attemptId}</dd>
                </div>
              </dl>
            </div>
          ) : (
            <div className="vp-callout" data-tone="unknown">
              <div className="vp-callout-title">No signed receipt</div>
              <div className="vp-callout-body">{record.receipt.limitation}</div>
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}
